// English strings for P7-D (CUSTOMER-03…06, AUTOMATION-01…04, segments) (D-013: English only). Owned by that worker;
// spread into `en` as `en.engagement`, so keys read `t("engagement.…")`. Copy rules: DESIGN_SYSTEM §9.6.
// Two sections: `admin` (vendor admin screens: consent, suppressions, privacy requests, segments) and
// `storefront` (shopper-facing islands, added separately).
export const engagementStrings = {
  admin: {
    common: {
      loadMore: "Show more",
      loading: "Loading…",
      masked: "Contact details are partly hidden for your role.",
      channels: { sms: "SMS", email: "Email" },
      allChannels: "All channels",
      channelLabel: "Channel",
      unknown: "Other",
    },
    links: {
      segments: "Segments",
      suppressions: "Suppression list",
      backToCustomers: "Back to customers",
      backToSegments: "Back to segments",
    },
    consent: {
      title: "Marketing consent",
      lead: "Marketing messages go only to channels with a current consent that isn't suppressed.",
      contact: "Contact",
      noContact: "No contact on file",
      allowed: "Can receive marketing",
      reasons: {
        no_consent: "No marketing consent",
        unsubscribed: "Unsubscribed",
      },
      historyTitle: "Consent history",
      historyCaption: "Consent history, newest first",
      historyEmptyTitle: "No consent records yet",
      historyEmptyBody:
        "Records appear when this customer opts in or out at checkout, through the newsletter or from a message.",
      columns: {
        channel: "Channel",
        purpose: "Purpose",
        granted: "Choice",
        status: "Status",
        source: "Source",
        textVersion: "Consent text",
        date: "Date",
      },
      granted: "Opted in",
      withdrawn: "Opted out",
      noTextVersion: "Not recorded",
      purposes: {
        marketing: "Marketing",
        order_updates: "Order updates",
        abandoned_checkout: "Checkout reminders",
        stock_alert: "Back-in-stock alerts",
      },
      statuses: {
        confirmed: "Confirmed",
        pending: "Waiting for email confirmation",
      },
      sources: {
        checkout: "Checkout",
        newsletter: "Newsletter form",
        newsletter_confirm: "Newsletter confirmation",
        stock_alert: "Back-in-stock form",
        unsubscribe_link: "Unsubscribe link",
        stop_keyword: "STOP reply",
        admin: "Staff",
      },
      suppressionsTitle: "Suppressions for this customer",
      suppressionsCaption: "Suppressions for this customer",
      suppressionsEmptyTitle: "Not suppressed",
      suppressionsEmptyBody: "No channel of this customer is on the suppression list.",
    },
    reasons: {
      customer_request: "Customer asked",
      complaint: "Complaint",
      bounce: "Bounced",
      other: "Other",
      unsubscribed: "Unsubscribed",
      stop_keyword: "STOP reply",
    },
    suppressions: {
      title: "Suppression list",
      lead: "Contacts here never receive marketing messages, whatever their consent says. Order updates still go out.",
      caption: "Suppressed contacts",
      filterLabel: "Show",
      columns: {
        channel: "Channel",
        contact: "Contact",
        reason: "Reason",
        source: "Added by",
        date: "Added",
      },
      emptyTitle: "No suppressed contacts",
      emptyBody: "Unsubscribes, STOP replies and contacts you add appear here.",
      add: "Add to suppression list",
      addTitle: "Add a contact to the suppression list",
      addLead: "This contact will stop receiving marketing messages on the channel you choose.",
      contactLabel: "Contact",
      contactHelpSms: "A Nepal mobile number, for example 98XXXXXXXX.",
      contactHelpEmail: "An email address.",
      contactRequired: "Enter the phone number or email address to suppress.",
      contactInvalid: "Enter a valid Nepal mobile number or email address for this channel.",
      reasonLabel: "Reason",
      added: "Contact added to the suppression list",
      sources: {
        admin: "Staff",
        unsubscribe_link: "Unsubscribe link",
        stop_keyword: "STOP reply",
        bounce: "Bounce",
        complaint: "Complaint",
      },
    },
    dsr: {
      title: "Privacy requests",
      lead: "Export everything this store holds about the customer, or remove their personal details.",
      export: "Export data",
      exporting: "Exporting…",
      exported: "Customer data downloaded",
      anonymise: "Anonymise customer",
      anonymising: "Anonymising…",
      anonymised: "Customer anonymised",
      anonymisedBadge: "Anonymised",
      anonymisedNote:
        "This customer was anonymised. Their name, phone, email and addresses are removed; order totals stay for your records.",
      confirmTitle: "Anonymise this customer?",
      confirmBody:
        "Their name, phone number, email address and saved addresses are removed from the customer and their orders, and their consents and wishlist are deleted. This can't be undone.",
      confirmKeeps:
        "Order numbers, totals and payment records are kept, so your sales and finance reports stay correct.",
      confirmButton: "Anonymise customer",
      openOrders:
        "This customer has an open order. Deliver, complete or cancel it first, then anonymise.",
      alreadyAnonymised: "This customer is already anonymised.",
      needsPermission: "Only staff who can delete customers can do this.",
      historyTitle: "Request history",
      historyCaption: "Privacy requests for this customer",
      historyEmptyTitle: "No privacy requests yet",
      historyEmptyBody: "Exports and anonymisations for this customer are listed here.",
      columns: {
        kind: "Request",
        state: "Result",
        requestedBy: "Asked by",
        requested: "Requested",
        completed: "Completed",
      },
      kinds: { export: "Data export", anonymize: "Anonymisation" },
      states: { completed: "Completed", refused: "Refused" },
      by: { staff: "Staff", customer: "Customer" },
    },
    segments: {
      title: "Segments",
      lead: "Group customers by what they buy, where they live and whether they agreed to marketing.",
      new: "New segment",
      newTitle: "New segment",
      caption: "Customer segments",
      columns: {
        name: "Name",
        members: "Customers",
        computed: "Last counted",
        state: "Status",
      },
      states: { idle: "Ready", computing: "Counting…" },
      neverComputed: "Not counted yet",
      emptyTitle: "No segments yet",
      emptyBody: "Create a segment to see which customers match a set of conditions.",
      members: { one: "{count} customer", other: "{count} customers" },
      notFound: "This segment doesn't exist any more.",
    },
    form: {
      secSegment: "Segment",
      namePh: "Segment name, e.g. Repeat buyers",
      match: { all: "Match all", any: "Match any" },
      ordersUnit: "orders",
      rs: "Rs",
      tagPh: "Tag, e.g. wholesale",
      districtPick: "Add a district…",
      customersMatch: { one: "customer matches now", other: "customers match now" },
      startFrom: "Start from",
      templates: {
        repeat: "Repeat buyers",
        big: "Big spenders",
        lapsed: "Not ordered in 90 days",
        sms: "SMS subscribers",
      },
      howItWorks:
        "Customers join or leave the segment as their orders change. Open the segment after saving to count its members.",
      needed: "Give the segment a name and finish its conditions.",
    },
    editor: {
      nameLabel: "Segment name",
      nameRequired: "Give the segment a name.",
      matchLabel: "Customers must match",
      matchAll: "All conditions",
      matchAny: "Any condition",
      conditionsLegend: "Conditions",
      conditionN: "Condition {n}",
      fieldLabel: "Condition {n}: what to check",
      opLabel: "Condition {n}: comparison",
      valueLabel: "Condition {n}: value",
      addCondition: "Add condition",
      removeCondition: "Remove condition {n}",
      limit: "A segment can have up to {max} conditions.",
      needOne: "Add at least one condition.",
      fields: {
        orders_count: "Number of orders",
        total_spent_paisa: "Total spent",
        last_order_days: "Last order",
        district: "District",
        tag: "Customer tag",
        marketing_consent: "Marketing consent",
      },
      ops: {
        gte: "At least",
        lte: "At most",
        eq: "Exactly",
        within: "Within the last",
        older_than: "More than",
        in: "Is one of",
        has: "Has",
      },
      days: "days ago",
      daysWithin: "days",
      districtSearch: "Search districts",
      districtAdd: "Add {name}",
      districtRemove: "Remove {name}",
      districtsSelected: { one: "{count} district", other: "{count} districts" },
      districtNoMatch: "No district matches that name.",
      problems: {
        count: "Enter a whole number, 0 or more.",
        days: "Enter a number of days from 1 to 3,650.",
        districts: "Pick at least one district.",
        tag: "Enter a tag of up to 40 characters.",
      },
      previewTitle: "Matching customers now",
      previewCount: { one: "{count} customer matches", other: "{count} customers match" },
      previewInvalid: "Finish the conditions to see how many customers match.",
      previewLoading: "Counting matching customers…",
      previewFailed: "Couldn't count matching customers. Change a condition to try again.",
      save: "Save segment",
      saving: "Saving…",
      create: "Create segment",
      creating: "Creating…",
      created: "Segment created",
      saved: "Segment saved",
      conflict:
        "Someone else changed this segment while you were editing. Reload to see their version, then make your change again.",
      reload: "Reload",
    },
    detail: {
      summaryTitle: "Summary",
      memberCount: "Customers in segment",
      lastComputed: "Last counted",
      state: "Status",
      compute: "Count customers",
      computing: "Counting…",
      computeStarted: "Counting started. The member list updates when it finishes.",
      computeActivation:
        "Counting segment members needs the background job queue, which isn't activated yet. The live preview count still works.",
      delete: "Delete segment",
      deleteTitle: "Delete this segment?",
      deleteBody:
        "The segment and its member list are deleted. Customers themselves aren't changed.",
      deleted: "Segment deleted",
      editTitle: "Conditions",
      membersTitle: "Customers in this segment",
      membersCaption: "Customers in this segment",
      membersEmptyTitle: "No customers listed yet",
      membersEmptyBody:
        "Count customers to fill this list. It shows who matched at the last count.",
      columns: {
        name: "Name",
        contact: "Contact",
        orders: "Orders",
        spent: "Spent",
        lastOrder: "Last order",
        marketing: "Marketing",
      },
      unnamed: "No name",
      marketingOn: "{channel} allowed",
      marketingOff: "No {channel}",
    },
  },
  storefront: {},
} as const;
